import { api, type ThreadFlagAccess, type ThreadFlagsResponse } from "@/lib/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";

/**
 * Shared writer for one Boo's thread-flag policy — used by the Extras cards
 * (the owning surface) and the People table's "Approves …" switches (the
 * secondary, scan-and-bulk surface). Optimistic: the UI reflects the change
 * instantly and rolls back on failure, and because each next payload is built
 * from the optimistically-updated cache, a second quick edit can't resurrect
 * pre-edit state from a stale snapshot. The contract requires the full policy
 * (access + approvers) on every PATCH.
 */
export function useSaveFlagPolicy(containerId: string) {
  const queryClient = useQueryClient();
  const queryKey = ["thread-flags", containerId];
  return useMutation({
    mutationFn: (policy: {
      flagId: string;
      access: ThreadFlagAccess;
      approvers: "everyone" | string[];
    }) => api.containers.flags.setAccess(containerId, policy),
    onMutate: async (policy) => {
      await queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData<ThreadFlagsResponse>(queryKey);
      queryClient.setQueryData<ThreadFlagsResponse>(queryKey, (old) =>
        old
          ? {
              ...old,
              flags: old.flags.map((f) =>
                f.id === policy.flagId
                  ? { ...f, access: policy.access, approvers: policy.approvers }
                  : f,
              ),
            }
          : old,
      );
      return { prev };
    },
    onError: (_err, _policy, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(queryKey, ctx.prev);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });
}
